/**
 * 卡片转出页面
 * 将卡片余额转出到账户
 */

import { useState, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Popup } from 'react-vant'
import Big from 'big.js'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import FixedBottom from '@/components/FixedBottom'
import CurrencySelectPopup, { type Currency } from '@/components/CurrencySelectPopup'
import { useCurrencyList } from '@/hooks'
import { formatThousands } from '@/utils'
import Divider from '@/components/Divider'
import { Toast } from 'react-vant'

// 卡片信息接口
interface CardInfo {
    maskedPan?: string
    balance?: number
    backgroundImage?: string
    cardIssued?: boolean
    status?: string
}

// 最小保留金额
const MIN_RESERVE_AMOUNT = 1

export default function CardWithdraw() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const cardId = searchParams.get('id') || ''
    const cardNo = searchParams.get('cardNo') || ''

    // 从路由 state 获取卡片信息
    const cardInfo: CardInfo = location.state || {}
    const cardBalance = cardInfo.balance || 0

    // 可转出余额（需要保留1 USD）
    const availableBalance = useMemo(() => {
        return Math.max(0, cardBalance - MIN_RESERVE_AMOUNT)
    }, [cardBalance])

    const { currencyList } = useCurrencyList()

    // TODO: 手续费需要从配置获取
    const fee = 0

    // 转出金额
    const [amount, setAmount] = useState('')
    // 选中的币种（到账币种）
    const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null)
    // 币种选择弹窗
    const [currencyPopupVisible, setCurrencyPopupVisible] = useState(false)
    // 确认弹窗
    const [confirmPopupVisible, setConfirmPopupVisible] = useState(false)

    // 当前选中的币种（默认选择 USDT 或第一个）
    const currentCurrency = useMemo(() => {
        if (selectedCurrency) {
            return selectedCurrency
        }
        // 优先选择 USDT
        const usdt = currencyList.find(c => c.symbol === 'USDT')
        if (usdt) {
            return { icon: usdt.icon, name: usdt.name, description: usdt.description, symbol: usdt.symbol }
        }
        // 否则选择第一个
        const first = currencyList[0]
        return first ? { icon: first.icon, name: first.name, description: first.description, symbol: first.symbol } : null
    }, [selectedCurrency, currencyList])

    // 处理金额输入
    const handleAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        if (/^\d*\.?\d{0,2}$/.test(value) || value === '') {
            setAmount(value)
        }
    }, [])

    // 计算实际转出金额（如果用户输入的金额等于可用余额，需要减去保留金额）
    const actualWithdrawAmount = useMemo(() => {
        if (!amount) return '0.00'
        const inputAmount = parseFloat(amount) || 0

        // 如果输入金额大于等于可用余额，实际转出金额为可用余额
        if (inputAmount >= availableBalance) {
            return availableBalance.toFixed(2)
        }

        return inputAmount.toFixed(2)
    }, [amount, availableBalance])

    // 计算到账金额（扣除手续费）
    const receivedAmount = useMemo(() => {
        if (!amount) return '0.00'
        try {
            const amountBig = new Big(actualWithdrawAmount)
            const feeBig = new Big(fee || 0)
            const result = amountBig.minus(feeBig)
            return result.lt(0) ? '0.00' : result.toFixed(2)
        } catch {
            return '0.00'
        }
    }, [actualWithdrawAmount, fee])

    // 检查是否可以转出
    const canWithdraw = useMemo(() => {
        const num = parseFloat(amount) || 0
        return num > 0 && num <= availableBalance
    }, [amount, availableBalance])

    // 点击转出按钮，显示确认弹窗
    const handleWithdrawClick = useCallback(() => {
        if (!canWithdraw || !currentCurrency) return
        setConfirmPopupVisible(true)
    }, [canWithdraw, currentCurrency])

    // 确认转出，跳转到选择验证方式页面
    const handleConfirmWithdraw = useCallback(() => {
        if (!canWithdraw || !currentCurrency || !cardId) return

        setConfirmPopupVisible(false)

        // 跳转到选择验证方式页面
        navigate('/card-v2/card-withdraw/select-verification', {
            state: {
                cardId,
                cardNo,
                amount: actualWithdrawAmount,
                currency: currentCurrency.symbol,
                receivedAmount,
                fee
            }
        })
    }, [cardId, cardNo, actualWithdrawAmount, currentCurrency, receivedAmount, fee, canWithdraw, navigate])

    // 选择币种
    const handleSelectCurrency = useCallback((currency: Currency) => {
        setSelectedCurrency(currency)
    }, [])

    return (
        <PageLayout>
            <Header
                leftChildren={<Back />}
                centerChildren={<span className="text-base font-medium">{t('转出')}</span>}
            />

            <div className="flex-1 flex flex-col px-5 pb-8">
                {/* 提示信息 - 始终显示 */}
                <div className="mt-4 px-4 py-3 bg-[#FDECED] rounded-xl flex items-center">
                    <img src="/images/card-v2/warning-tips.svg" alt="" className="w-5 h-5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-[#F5222D]">
                        {t('请至少在卡片上保留 {{amount}} USD', { amount: MIN_RESERVE_AMOUNT })}
                    </span>
                </div>

                {/* 转出金额输入 */}
                <div className="mt-6">
                    <div className="text-sm text-[#1D2129] font-medium mb-3">
                        {t('转出金额')}
                    </div>
                    <div className="flex items-center justify-between border-b border-[#E5E6EB] pb-4">
                        <input
                            type="text"
                            inputMode="decimal"
                            className="w-0 flex-1 min-w-0 text-8 font-semibold text-[#C9CDD4] bg-transparent outline-none placeholder-[#C9CDD4]"
                            value={amount}
                            onChange={handleAmountChange}
                            maxLength={18}
                            placeholder="0.00"
                            style={{ color: amount ? '#1D2129' : '#C9CDD4' }}
                        />
                        <div className="flex-shrink-0 flex items-center bg-[#F7F8FA] rounded-full px-3 py-2 ml-3">
                            <div className="w-5 h-5 rounded-full bg-[#E5E6EB] mr-2 flex items-center justify-center overflow-hidden">
                                <img src="/images/home/usd.png" alt="USD" className="w-full h-full object-cover" />
                            </div>
                            <span className="text-sm font-medium text-[#1D2129]">USD</span>
                        </div>
                    </div>
                    <div className="text-sm text-[#86909C] mt-2">
                        {t('可转出余额')}  {formatThousands(availableBalance, 2, true)} USD
                    </div>
                </div>
                <Divider type="solid" className='mt-4' />

                {/* 到账金额 */}
                <div className="mt-8">
                    <div className="text-sm text-[#1D2129] font-medium mb-3">
                        {t('到账金额')}
                    </div>
                    <div className="flex items-center justify-between border-b border-[#E5E6EB] pb-4">
                        <div className="text-8 font-semibold text-ellipsis overflow-hidden" style={{ color: amount ? '#1D2129' : '#C9CDD4' }}>
                            {formatThousands(receivedAmount, 2, true)}
                        </div>
                        {currentCurrency && (
                            <div
                                className="flex-shrink-0 flex items-center bg-[#F7F8FA] rounded-full px-3 py-2 cursor-pointer ml-3"
                                onClick={() => setCurrencyPopupVisible(true)}
                            >
                                <div className="w-5 h-5 rounded-full mr-2 flex items-center justify-center overflow-hidden">
                                    <img src={currentCurrency.icon} alt={currentCurrency.symbol} className="w-full h-full object-cover" />
                                </div>
                                <span className="text-sm font-medium text-[#1D2129] mr-2">{currentCurrency.symbol}</span>
                                <img src="/images/card-v2/select-down.svg" alt="down" />
                            </div>
                        )}
                    </div>
                </div>
                <Divider type="solid" className='mt-4' />

                {/* 转出按钮 */}
                <FixedBottom>
                    <CustomButton
                        onClick={handleWithdrawClick}
                        disabled={!canWithdraw}
                    >
                        {t('转出')}
                    </CustomButton>
                </FixedBottom>
            </div>

            {/* 币种选择弹窗 */}
            <CurrencySelectPopup
                visible={currencyPopupVisible}
                onClose={() => setCurrencyPopupVisible(false)}
                onSelect={handleSelectCurrency}
                currencies={currencyList}
            />

            {/* 转出确认弹窗 */}
            <Popup
                visible={confirmPopupVisible}
                onClose={() => setConfirmPopupVisible(false)}
                position="bottom"
                round
                closeable
                closeIcon={<img src="/icon/close.svg" alt="" />}
                closeIconPosition="top-right"
            >
                <div className="px-5 pt-6 pb-8">
                    <div className="text-lg font-semibold text-[#1D2129] mb-8">
                        {t('信息确认')}
                    </div>

                    <div className="flex flex-col items-center mb-8">
                        <div className="w-16 h-16 rounded-full bg-[#F7F8FA] flex items-center justify-center mb-4">
                            <img src="/images/card-v2/card-transfer-icon.svg" alt="" className="w-8 h-8" />
                        </div>
                        <div className="text-4xl font-semibold text-[#1D2129]">
                            -{formatThousands(parseFloat(actualWithdrawAmount) || 0, 2, true)}
                        </div>
                    </div>

                    <div className="space-y-4 mb-8">
                        <div className="flex items-center justify-between">
                            <span className="text-base text-[#86909C]">{t('收款账户')}</span>
                            <span className="text-base font-medium text-[#1D2129]">{currentCurrency?.symbol}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-base text-[#86909C]">{t('手续费')}</span>
                            <span className="text-base font-medium text-[#1D2129]">{fee}%</span>
                        </div>
                    </div>

                    <CustomButton onClick={handleConfirmWithdraw}>
                        {t('确定')}
                    </CustomButton>
                </div>
            </Popup>
        </PageLayout>
    )
}

